import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Guarantee } from '@/components/booking/landSteps';
import { clearBookDraft } from '@/lib/bookFlow';
import { CheckCircle2, Loader2, Clock } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function VacantLandSuccess() {
  const { isAuthenticated, loading } = useAuth();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const orderId = params.get('order_id');
  const sessionId = params.get('session_id');

  useEffect(() => {
    if (!isAuthenticated || !orderId || !sessionId) return undefined;
    let tries = 0;
    let timer;
    const check = async () => {
      try {
        const res = await authFetch(`${API_BASE}/api/vacant-land/orders/${orderId}/confirm?session_id=${encodeURIComponent(sessionId)}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Could not confirm payment');
        setInfo(data);
        if (data.paid) {
          clearBookDraft('land');
        } else if (tries++ < 8) timer = setTimeout(check, 2000);
      } catch (e) { setError(e.message); }
    };
    check();
    return () => clearTimeout(timer);
  }, [isAuthenticated, orderId, sessionId]);

  if (loading || (!info && !error)) {
    return <div className="min-h-screen bg-[#050816] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#C6A14A]" /></div>;
  }
  const paid = info?.paid;
  return (
    <div className="min-h-screen bg-[#050816] flex items-center justify-center px-6 py-16" data-testid="land-success">
      <div className="max-w-md w-full text-center space-y-4">
        {paid ? <CheckCircle2 className="h-14 w-14 text-[#C6A14A] mx-auto" /> : <Clock className="h-14 w-14 text-[#E8E4DE]/50 mx-auto" />}
        <h1 className="font-serif text-3xl text-white" data-testid="land-success-title">{paid ? "You're booked!" : error ? 'We could not confirm your payment' : 'Confirming your payment…'}</h1>
        {info && (
          <p className="text-[#E8E4DE]/70">
            {info.title} · {info.band_label}{info.add_video ? ' + aerial video' : ''}
            {paid && info.total_cents ? ` · $${(info.total_cents / 100).toFixed(2)} paid` : ''}
          </p>
        )}
        {error && <p className="text-red-400 text-sm">{error}</p>}
        {paid && <p className="text-[#E8E4DE]/60 text-sm">We'll confirm your shoot date shortly. A receipt is on its way to your email.</p>}
        <Guarantee />
        <Button asChild className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]" data-testid="land-success-orders-btn"><Link to="/orders">View my orders</Link></Button>
      </div>
    </div>
  );
}
